import React, { useEffect } from 'react';
import JsBarcode from 'jsbarcode'; // Asegúrate de instalar esta librería: npm install jsbarcode
import { FaPrint, FaTimes } from 'react-icons/fa';
import formatAplicaciones from '../../../utils/AplicacionesFormat';

/**
 * Componente para generar y previsualizar una etiqueta doble de producto para impresión.
 * @param {object} producto - El objeto del producto a imprimir.
 * @param {function} onClose - Función para cerrar el modal de impresión.
 */
const ImpresionEtiqueta = ({ producto, onClose }) => {

  useEffect(() => {
    if (producto && producto.codigo_barras) {
      try {
        const barcodeOptions = {
          format: 'CODE128',
          lineColor: '#000',
          width: 1.2,
          height: 30,
          displayValue: false,
          margin: 0,
        };
        JsBarcode('#barcode-left', producto.codigo_barras, barcodeOptions);
        JsBarcode('#barcode-right', producto.codigo_barras, barcodeOptions);
      } catch (e) {
        console.error('Error al generar el código de barras:', e);
      }
    }
  }, [producto]);

  const handlePrint = () => window.print();
  
  const truncateProductName = (name) => {
    if (!name) return '';
    const maxLength = 47;
    return name.length > maxLength ? `${name.substring(0, maxLength - 3)}...` : name;
  };

  if (!producto) return null;

  const renderLabelContent = (id) => {
    // Manejar hasta tres líneas de aplicaciones usando el formateador importado
    const apps = producto.aplicaciones_vehiculo ? formatAplicaciones(producto.aplicaciones_vehiculo).split(' / ').slice(0, 4) : [];
    const hasApps = apps.length > 0;
    const hasMeasures = !!producto.medidas;
    const nameLength = truncateProductName(producto.nombre_producto).length;

    // Lógica de tamaño de fuente más dinámica
    let productNameSize = 15;
    let aplicacionSize = 15;
    let medidasSize = 12;

    if (nameLength > 30) productNameSize = 13;
    else if (nameLength > 20) productNameSize = 14;

    // Ajustar dinámicamente basado en la cantidad de contenido
    if (apps.length === 1) {
        aplicacionSize = 15;
        if (!hasMeasures) aplicacionSize = 15; // Más grande si es el único campo
    } else if (apps.length === 2) {
        aplicacionSize = 11;
        medidasSize = 10;
    } else if (apps.length >= 3) {
        productNameSize -= 2;
        aplicacionSize = 12;
        medidasSize = 9;
    }

    if (!hasApps && hasMeasures) {
        medidasSize = 14; // Medidas grandes si no hay aplicaciones
    }
    
    productNameSize = Math.max(productNameSize, 11);
    aplicacionSize = Math.max(aplicacionSize, 9);
    medidasSize = Math.max(medidasSize, 9);


    return (
        <div className="label-container bg-white">
            {/* Contenido superior se ajusta dinámicamente */}
            <div className='top-content'>
                <p className="product-name font-bold text-center" style={{ fontSize: `${productNameSize}px` }}>
                    {truncateProductName(producto.nombre_producto)}
                </p>
                {apps.map((appLine, index) => (
                    <p key={index} className="aplicacion text-center font-semibold" style={{ fontSize: `${aplicacionSize}px` }}>
                        {appLine}
                    </p>
                ))}
                {hasMeasures && (
                    <p className="medidas font-bold text-center" style={{ fontSize: `${medidasSize}px` }}>
                        {producto.medidas}
                    </p>
                )}
            </div>

            {/* Contenido inferior con código de barras */}
            <div className='bottom-content'>
                <div className="bottom-info-container">
                    <span className="barcode-text font-mono font-semibold">
                        {producto.codigo_barras}
                    </span>
                    <span className="sku font-mono font-semibold">
                        {producto.sku}
                    </span>
                </div>
                <div className="barcode-wrapper flex justify-center items-center">
                    <svg id={id}></svg>
                </div>
            </div>
        </div>
    );
  };

  return (
    <>
      <style>{`
        .print-modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex; justify-content: center; align-items: center; z-index: 1000;
        }
        .print-modal-content {
          background-color: #f0f0f0; padding: 20px; border-radius: 8px;
        }
        #printable-area {
          display: flex;
          gap: 2mm; /* Espacio reducido entre etiquetas */
          width: 100mm;
          height: 45mm; /* Altura total reducida */
          box-sizing: border-box;
          padding: 2mm 1mm; /* Padding reducido */
          border: 1px dashed grey;
        }
        .label-container {
          width: 47.5mm; /* Ancho ajustado para el nuevo gap y padding */
          height: 41mm; /* Altura de la etiqueta interna reducida */
          display: flex;
          flex-direction: column;
          font-family: 'Arial', sans-serif;
          overflow: hidden;
        }
        .top-content {
            display: flex;
            flex-direction: column;
            justify-content: flex-start; /* Alinea el contenido hacia arriba */
            overflow: hidden; /* Previene que el texto se desborde */
        }
        .bottom-content {
            /* margin-top: auto; -- KEY CHANGE: Removido para eliminar el gap */
        }
        .product-name, .aplicacion, .medidas {
            line-height: 1.1; /* Interlineado compacto */
            margin: 0;
        }
        .barcode-wrapper { 
            height: 12mm;
            overflow: hidden; 
            /* margin-bottom: 0.5mm; -- REMOVED for tighter spacing */
        }
        .bottom-info-container {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            padding: 0 0.5mm;
            margin-bottom: 0.5mm; /* Espacio MÍNIMO entre el texto y el código de barras SVG */
        }
        .barcode-text, .sku { 
            font-size: 10px; 
            letter-spacing: 0.5px;
        }

        @media print {
          /* Reset page margins */
          @page {
            size: 100mm 45mm;
            margin: 0;
          }

          /* Reset body/html avoid any space inherited from parent layouts */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            min-height: 0 !important;
            height: auto !important;
          }

          /* Hide EVERYTHING by default */
          body * { visibility: hidden; }

          /* Ensure the overlay and printable area are visible and reset their layout */
          .print-modal-overlay {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block !important; /* Disable flex centering */
            background: white !important;
            visibility: visible !important;
            z-index: 9999 !important;
          }

          .print-modal-content {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            visibility: visible !important;
          }

          #printable-area, #printable-area * {
            visibility: visible !important;
          }

          #printable-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
            display: flex !important;
            gap: 2mm;
            padding: 2mm 1mm;
            width: 100mm !important;
            height: 45mm !important;
            box-sizing: border-box !important;
          }

          /* Force hide specific global components that might interfere */
          .no-print, header, footer, .ToastContainer {
            display: none !important;
          }
        }

      `}</style>
      <div className="print-modal-overlay">
        <div className="print-modal-content">
          <div className="flex justify-between items-center no-print mb-4">
            <h2 className="text-xl font-bold">Previsualización de Etiqueta</h2>
            <button onClick={onClose} className="text-red-bootik hover:text-red-700">
                <FaTimes size={24} />
            </button>
          </div>
          <div id="printable-area">
            {renderLabelContent("barcode-left")}
            {renderLabelContent("barcode-right")}
          </div>
          <div className="flex justify-center space-x-4 mt-6 no-print">
            <button onClick={handlePrint} className="bg-blue-500 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-600 flex items-center space-x-2">
              <FaPrint /> <span>Imprimir</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImpresionEtiqueta;

